import { getType, isActionOf } from 'typesafe-actions'
import { from, of } from 'rxjs'
import { catchError, exhaustMap, filter, map, mergeMap, switchMap } from 'rxjs/operators'
import { toast } from 'react-toastify'
import { AnyAction } from 'redux'
import { RootEpic } from '../types'
import {
  getCommissionsRequest,
  getCommissionsSuccess,
  getCommissionsFailure,
  approveCommissionsRequest,
  approveCommissionsSuccess,
  approveCommissionsFailure,
  rejectCommissionsRequest,
  rejectCommissionsSuccess,
  rejectCommissionsFailure,
  updateCommissionsRequest,
  updateCommissionsSuccess,
  updateCommissionsFailure,
} from '../actions'

export const fetchCommissionsEpic: RootEpic = (action$, state$, { fetchCommissions }) =>
  action$.pipe(
    filter(isActionOf([getCommissionsRequest])),
    switchMap(({ type, payload }) =>
      from(
        fetchCommissions({
          token: state$.value.app.authToken as string,
          limit: 10,
          ...state$.value.commissions.filters,
          ...(type === getType(getCommissionsRequest) && payload),
        }),
      ).pipe(
        map((response) => getCommissionsSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch commissions.')
          return of(getCommissionsFailure(message))
        }),
      ),
    ),
  )

export const approveCommissionsEpic: RootEpic = (action$, state$, { approveCommissions }) =>
  action$.pipe(
    filter(isActionOf([approveCommissionsRequest])),
    exhaustMap(({ payload }) =>
      from(
        approveCommissions({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          const actions: Array<AnyAction> = []
          if (response.success) {
            toast.success('Commissions successfully approved.')
            actions.push(getCommissionsRequest({ page: 1 }))
          }
          if (response.alreadyApproved && Object.keys(response.alreadyApproved).length > 0) {
            const message = Object.entries(response.alreadyApproved).map(([id, reason]) => `ID ${id}: ${reason}`)

            toast.warning(`Some commissions were not approved:\n\n${message.join('\n\n')}`)
          }
          actions.push(approveCommissionsSuccess(response))
          return of(...actions)
        }),
        catchError((message) => {
          toast.error('Failed to approve commissions.')
          return of(approveCommissionsFailure(message))
        }),
      ),
    ),
  )

export const updateCommissionsEpic: RootEpic = (action$, state$, { updateCommissions }) =>
  action$.pipe(
    filter(isActionOf([updateCommissionsRequest])),
    exhaustMap(({ payload }) =>
      from(
        updateCommissions({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          const actions: Array<AnyAction> = []
          if (response.success) {
            toast.success('Commissions successfully updated.')
            actions.push(getCommissionsRequest({ page: 1 }))
          }
          if (response.failedUpdates && Object.keys(response.failedUpdates).length > 0) {
            const messages = Object.entries(response.failedUpdates).map(([id, reason]) => `#${id}: ${reason}`)

            toast.warning(`Some commissions were not updated:\n${messages.join('\n')}`)
          }
          actions.push(updateCommissionsSuccess(response))
          return of(...actions)
        }),
        catchError((message) => {
          toast.error('Failed to update commissions.')
          return of(updateCommissionsFailure(message))
        }),
      ),
    ),
  )

export const rejectCommissionsEpic: RootEpic = (action$, state$, { rejectCommissions }) =>
  action$.pipe(
    filter(isActionOf([rejectCommissionsRequest])),
    exhaustMap(({ payload }) =>
      from(
        rejectCommissions({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          const actions: Array<AnyAction> = []
          if (response.success) {
            toast.success('Commissions successfully rejected.')
            actions.push(getCommissionsRequest({ page: 1 }))
          }
          if (response.alreadyHandled && Object.keys(response.alreadyHandled).length > 0) {
            const messages = Object.entries(response.alreadyHandled).map(([id, reason]) => `#${id}: ${reason}`)

            toast.warning(`Some commissions were not rejected:\n${messages.join('\n')}`)
          }
          actions.push(rejectCommissionsSuccess(response))
          return of(...actions)
        }),
        catchError((message) => {
          toast.error('Failed to reject commissions.')
          return of(rejectCommissionsFailure(message))
        }),
      ),
    ),
  )

export default [fetchCommissionsEpic, approveCommissionsEpic, rejectCommissionsEpic, updateCommissionsEpic]
